import { useCallback, useContext, useEffect, useRef, useState } from "react";
import TabPanel from "../../../../components/UI/Tabs/TabPanel";
import SelectedArray from "./SelectedArray";
import { uvenuFetcher } from "../../../../utils/uvenu-fetcher";
import { getControllableRoomId, URLS } from "../../../../constants";
import UvDialog from "../../../../components/UI/UvDialog";
import { TextField } from "@mui/material";
import ConfirmationDialog from "../../../../components/UI/ConfirmationDialog";
import { AppContext } from "../../../../contexts/App.Context";
const SelectedArrayContainer = (props) => {
  const appContext = useContext(AppContext);
  const { eventId, loggedInUser, socket } = props;
  const defaultSelection = {
    name: "Media Items",
    items: [],
    layouts: [],
    eventId,
  };
  const [selectedArrays, setSelectedArrays] = useState([defaultSelection]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [name, setName] = useState(selectedArrays[activeTabIndex]?.name || "");
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updatedName, setUpdatedName] = useState("");
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const [isSocketConnected, setIsSocketConnected] = useState(
    socket?.connected || null
  );

  const updateTabId = useRef(null);
  const lastEmitHash = useRef(null);

  const socketEmit = useCallback((dataToEmit) => {
    if (lastEmitHash.current === JSON.stringify(dataToEmit)) {
      return;
    }
    lastEmitHash.current = JSON.stringify(dataToEmit);
    socket.emit("data", dataToEmit);
  }, []);
  const getSelectedItems = async () => {
    try {
      const response = await uvenuFetcher({
        method: "GET",
        url: URLS.GET_SELECTED_CONTROLLABLE_ITEMS(eventId),
        headers: { token: loggedInUser.token },
      });
      if (response.statusCode === 200) {
        return response.json.selectedState;
      } else {
        return {
          items: [],
          layouts: [],
        };
      }
    } catch (e) {
      console.log(e);
      return {
        items: [],
        layouts: [],
      };
    }
  };

  const createTab = async (payload) => {
    const response = await uvenuFetcher({
      method: "POST",
      url: URLS.SAVE_SELECTED_CONTROLLABLE_ITEMS,
      headers: { token: loggedInUser.token },
      body: defaultSelection,
    });
  };

  const onSave = async () => {
    const payload = {
      name,
      items: [],
      layouts: [],
      eventId,
    };
    const response = await uvenuFetcher({
      method: "POST",
      url: URLS.SAVE_SELECTED_CONTROLLABLE_ITEMS,
      headers: { token: loggedInUser.token },
      body: payload,
    });
    setShowNameDialog(false);
    if (response && response.statusCode === 201) {
      refresh(response.json.selectedItems._id);
    } else {
      appContext.triggerToast(true, {
        type: "error",
        message: response.json.message,
      });
    }
  };
  const onUpdate = async () => {
    const payload = { _id: updateTabId.current, eventId };
    payload["name"] = updatedName;
    const response = await uvenuFetcher({
      method: "PATCH",
      url: URLS.SAVE_SELECTED_CONTROLLABLE_ITEMS,
      headers: { token: loggedInUser.token },
      body: payload,
    });
    setShowUpdateDialog(false);
    if (response && response.statusCode === 201) {
      refresh(updateTabId.current);
    } else {
      appContext.triggerToast(true, {
        type: "error",
        message: response.json.message,
      });
    }
  };
  const deleteLastMessage = async () => {
    try {
      const response = await uvenuFetcher({
        method: "DELETE",
        url: URLS.DELETE_LAST_MESSAGE(getControllableRoomId(eventId)),
        headers: {
          token: loggedInUser.token,
        },
      });
    } catch (error) {
      console.log(error);
    }
  };
  let timeout = useRef(null);
  useEffect(() => {
    socket.on("connect", () => {
      socket.emit("joinRoom", getControllableRoomId(eventId));
      setIsSocketConnected(true);
    });

    socket.on("disconnect", () => {
      setIsSocketConnected(false);
      clearTimeout(timeout.current);
      timeout.current = setTimeout(() => {
        deleteLastMessage();
      }, 1000);
    });

    window.addEventListener("beforeunload", deleteLastMessage);

    return () => {
      socket.off("connect");
      socket.off("disconnect");
      window.removeEventListener("beforeunload", deleteLastMessage);
      deleteLastMessage();
      clearTimeout(timeout.current);
      timeout.current = null;
    };
  }, []);

  useEffect(() => {
    if (eventId) {
      getSelectedItems().then((savedState) => {
        if (savedState.length) {
          setSelectedArrays(savedState);
        } else {
          createTab(defaultSelection).then(async () => {
            const response = await getSelectedItems();
            if (response.length) {
              setSelectedArrays(response);
            } else {
              setSelectedArrays([defaultSelection]);
            }
          });
        }
      });
    }
  }, [eventId]);

  const handleAddTab = () => {
    setName(`Tab ${selectedArrays.length + 1}`);
    setShowNameDialog(true);
  };
  const refresh = async (activeTabId) => {
    const savedState = await getSelectedItems();
    if (savedState.length) {
      setSelectedArrays(savedState);
    }
    if (activeTabId) {
      savedState.forEach((selections, index) => {
        if (selections._id === activeTabId) {
          setActiveTabIndex(index);
        }
      });
    } else {
      setActiveTabIndex(0);
    }
  };

  const getTabs = () => {
    return selectedArrays.map((selections, index) => {
      if (!selections.name) {
        selections.name = `Tab ${index + 1}`;
      }
      return {
        id: selections._id,
        label: selections.name,
        content: (
          <SelectedArray
            {...props}
            selections={selections}
            refresh={refresh}
            socketEmit={socketEmit}
            isSocketConnected={isSocketConnected}
            activeTabIndex={activeTabIndex}
            index={index}
          />
        ),
      };
    });
  };
  const deleteTab = async () => {
    try {
      const response = await uvenuFetcher({
        method: "DELETE",
        url: URLS.DELETE_TAB(updateTabId.current),
        headers: {
          token: `${loggedInUser.token}`,
        },
      });
      if (response.statusCode === 200) {
        updateTabId.current = null;
        setShowConfirmationDialog(false);
        refresh();
      } else {
        return "error: " + response.json.message;
      }
    } catch {}
  };
  const handleEditTab = (tabData) => {
    updateTabId.current = tabData.id;
    setUpdatedName(tabData.label);
    setShowUpdateDialog(true);
  };
  const handleDeleteTab = async (tabData) => {
    updateTabId.current = tabData.id;
    setShowConfirmationDialog(true);
  };
  const getDeleteMessage = () => {
    let name = "";
    selectedArrays.forEach((selections) => {
      if (selections._id === updateTabId.current) {
        name = selections.name;
      }
    });
    if (!name) {
      return "error: Tab not found";
    }
    return `Are you sure you want to delete "${name}"?`;
  };
  const onTabChange = (index) => {
    setActiveTabIndex(index);
    const selectedTab = selectedArrays[index];
    if (selectedTab?.items[0]) {
      socketEmit({ activeItem: selectedTab?.items[0] });
    }
  };
  return (
    <div>
      <TabPanel
        tabs={getTabs()}
        onTabChange={onTabChange}
        addTab={true}
        onAddTabClick={handleAddTab}
        activeTabIndex={activeTabIndex}
        onEditTabClick={handleEditTab}
        onDeleteTabClick={handleDeleteTab}
      />
      <UvDialog
        key="add"
        show={showNameDialog}
        onClose={() => {
          setName("");
          setShowNameDialog(false);
        }}
        onSubmit={onSave}
        title="Save Tab Name"
        submitButtonLable="Add New Tab"
      >
        <TextField
          label="Name"
          variant="outlined"
          fullWidth
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          inputProps={{
            autoFocus: true,
          }}
          autoFocus={true}
          margin="normal"
          sx={{ marginBottom: "20px" }}
        />
      </UvDialog>
      <UvDialog
        key="update"
        show={showUpdateDialog}
        onClose={() => {
          setUpdatedName("");
          setShowUpdateDialog(false);
        }}
        onSubmit={onUpdate}
        title="Update Tab Name"
        submitButtonLable="Update"
      >
        <TextField
          label="Name"
          variant="outlined"
          fullWidth
          value={updatedName}
          onChange={(e) => {
            setUpdatedName(e.target.value);
          }}
          inputProps={{
            autoFocus: true,
          }}
          autoFocus={true}
          margin="normal"
          sx={{ marginBottom: "20px" }}
        />
      </UvDialog>
      <ConfirmationDialog
        title="Delete Confirmation"
        confirmButtonText="Delete"
        message={getDeleteMessage()}
        show={showConfirmationDialog}
        onConfirm={deleteTab}
        onClose={() => {
          setShowConfirmationDialog(false);
        }}
      />
    </div>
  );
};
export default SelectedArrayContainer;
